import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, FileSpreadsheet, FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OpsBriefDownload } from '../../types/investigator';

/** Rows shown per sheet in the Excel preview; the file has them all. */
export const EXCEL_PREVIEW_ROWS = 200;

type Kind = 'excel' | 'pdf';

/** Shared chrome for the two preview-open buttons. */
const OPEN_BUTTON = { variant: 'outline', size: 'sm', className: 'gap-1.5' } as const;

/**
 * The whole list of a Zoe answer as Excel or PDF (T136), previewed before
 * anything is saved: the PDF as the file itself, the Excel as its sheets'
 * rows (a workbook can't be shown in the browser). Download sits in the
 * preview, so a file is only saved when the user asks for it.
 */
export default function ZoeListDownloadPreview({ download }: { download: OpsBriefDownload }) {
  const [open, setOpen] = useState<Kind | null>(null);
  const [busy, setBusy] = useState<Kind | null>(null);
  const [failed, setFailed] = useState(false);
  const [pdf, setPdf] = useState<{ url: string; blob: Blob } | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  /** The Excel or PDF button that opened the preview: focus goes back to it on close. */
  const openerRef = useRef<HTMLElement | null>(null);
  /**
   * False once unmounted, so a PDF finished after the card went away is dropped.
   * Set on every mount: React's Strict Mode (dev) unmounts and remounts once, and
   * a flag left false then dropped every PDF ("Preparing…" forever).
   */
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const dropPdf = useCallback(() => {
    setPdf((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);
  useEffect(() => dropPdf, [dropPdf]);

  const close = useCallback(() => {
    setOpen(null);
    dropPdf();
    openerRef.current?.focus();
  }, [dropPdf]);

  // Keyboard: focus moves into the preview, Tab stays inside it, Escape closes it.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')];
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const preview = useCallback(async (kind: Kind) => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setFailed(false);
    if (kind === 'excel') {
      setOpen('excel');
      return;
    }
    setBusy('pdf');
    try {
      // Loaded on click: the PDF library stays off the panel's first load.
      const files = await import('../../utils/zoeListDownload');
      const blob = (await files.buildZoeListPdf(download)).output('blob');
      if (!liveRef.current) return;
      setPdf((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return { url: URL.createObjectURL(blob), blob };
      });
      setOpen('pdf');
    } catch (err) {
      console.error('[ZoeListDownloadPreview] The PDF could not be made', err);
      if (liveRef.current) setFailed(true);
    } finally {
      if (liveRef.current) setBusy(null);
    }
  }, [download]);

  const save = useCallback(async () => {
    setFailed(false);
    try {
      if (open === 'pdf' && pdf) {
        const a = document.createElement('a');
        a.href = pdf.url;
        a.download = `${download.filename}.pdf`;
        a.click();
      } else if (open === 'excel') {
        setBusy('excel');
        const files = await import('../../utils/zoeListDownload');
        files.downloadZoeListExcel(download);
      }
    } catch (err) {
      console.error(`[ZoeListDownloadPreview] The ${open} file could not be saved`, err);
      setFailed(true);
    } finally {
      setBusy(null);
    }
  }, [open, pdf, download]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground zoe-snap-in">
        <Download className="w-3.5 h-3.5" aria-hidden />
        <span>Full list{download.period ? `, ${download.period}` : ''}:</span>
        <Button type="button" {...OPEN_BUTTON} disabled={busy !== null} onClick={() => void preview('excel')}>
          <FileSpreadsheet className="w-3.5 h-3.5 text-success" aria-hidden />
          Excel
        </Button>
        <Button type="button" {...OPEN_BUTTON} disabled={busy !== null} onClick={() => void preview('pdf')}>
          <FileText className="w-3.5 h-3.5 text-destructive" aria-hidden />
          {busy === 'pdf' && !open ? 'Preparing…' : 'PDF'}
        </Button>
        {failed && !open ? <span className="text-warning">The file could not be made; try again.</span> : null}
      </div>

      {open
        ? createPortal(
            <div
              className="fixed inset-0 z-modal flex items-center justify-center p-2 sm:p-3 bg-black/70"
              role="dialog"
              aria-modal="true"
              aria-labelledby="zoe-list-preview-title"
              ref={dialogRef}
            >
              <div className="bg-popover border border-border rounded-xl shadow-2xl w-full max-w-modal h-modal flex flex-col overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border shrink-0">
                  <h2 id="zoe-list-preview-title" className="text-lg font-semibold text-foreground truncate pr-2">
                    {open === 'pdf' ? 'PDF' : 'Excel'} preview: {download.title}
                    {download.period ? `, ${download.period}` : ''}
                  </h2>
                  <Button
                    ref={closeRef}
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={close}
                    aria-label="Close preview"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                {open === 'pdf' && pdf ? (
                  <iframe title="PDF preview" src={`${pdf.url}#toolbar=0`} className="w-full flex-1 min-h-0 bg-background border-0" />
                ) : (
                  <ExcelPreview download={download} />
                )}
                <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-border bg-background/90 shrink-0">
                  {failed ? <span className="text-xs text-warning mr-auto">The file could not be saved; try again.</span> : null}
                  <Button
                    type="button"
                    variant="secondary"
                    className="border border-border"
                    onClick={close}
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    className="border border-primary"
                    loading={busy === 'excel'}
                    disabled={busy !== null}
                    onClick={() => void save()}
                  >
                    <Download className="w-4 h-4" aria-hidden />
                    {open === 'pdf' ? 'Download PDF' : 'Download Excel'}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** The workbook's sheets as tabs: Summary, then one per group, as the file lays them out. */
function ExcelPreview({ download }: { download: OpsBriefDownload }) {
  const [tab, setTab] = useState(0);
  const sheets = [{ title: 'Summary', count: undefined as number | undefined }, ...download.groups.map((g) => ({ title: g.title, count: g.count }))];
  const group = tab > 0 ? download.groups[tab - 1] : undefined;
  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div role="tablist" className="flex gap-1 overflow-x-auto px-4 pt-3 border-b border-border shrink-0">
        {sheets.map((s, i) => (
          <Button
            key={`${s.title}-${i}`}
            type="button"
            role="tab"
            aria-selected={tab === i}
            onClick={() => setTab(i)}
            variant={tab === i ? 'secondary' : 'ghost'}
            size="sm"
            className={`h-auto py-1.5 rounded-t-md border border-b-0 ${
              tab === i
                ? 'border-border bg-secondary text-foreground hover:bg-secondary'
                : 'border-transparent hover:bg-transparent'
            }`}
          >
            {s.title}
            {s.count !== undefined ? ` (${s.count})` : ''}
          </Button>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-4">
        {group ? (
          <>
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-secondary">
                <tr>
                  {download.columns.map((c) => (
                    <th key={c.label} className={`px-2 py-1.5 border border-border font-semibold text-foreground ${c.numeric ? 'text-right' : 'text-left'}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.rows.slice(0, EXCEL_PREVIEW_ROWS).map((row, r) => (
                  <tr key={r} className={r % 2 ? 'bg-card/40' : ''}>
                    {row.map((v, c) => (
                      <td key={c} className={`px-2 py-1 border border-border text-foreground/90 align-top ${download.columns[c]?.numeric ? 'text-right tabular-nums' : ''}`}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {group.rows.length > EXCEL_PREVIEW_ROWS ? (
              <p className="text-xs text-muted-foreground mt-2">
                Showing the first {EXCEL_PREVIEW_ROWS} of {group.rows.length} rows; the Excel file has all of them.
              </p>
            ) : null}
          </>
        ) : (
          <table className="text-sm border-collapse">
            <tbody>
              {[
                ['Question', download.question],
                ['Period', download.period],
                ['Checked at (UK time)', download.checked_at],
                ...download.summary.map((s) => [s.label, s.value] as const),
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <tr key={k}>
                    <td className="pr-6 py-1 text-muted-foreground align-top">{k}</td>
                    <td className="py-1 text-foreground">{v}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
        {!group && download.note ? <p className="text-xs text-muted-foreground mt-3">{download.note}</p> : null}
      </div>
    </div>
  );
}
